import React, {useState, useCallback, useRef} from "react";
import {Radio, DatePicker} from "antd";
import {FormItem, Input} from "formik-antd";
import {useTranslation} from "react-i18next";
import {TagOutlined, UserOutlined, CalendarOutlined, ShoppingCartOutlined, CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined} from "@ant-design/icons";
import {ArtworkIcon} from "../helpers/icons";
import day from "dayjs";
import {getLocale} from "./DateRangePicker";
import * as Yup from "yup";
import api from "../api";

export const tagTypeOptions = (t) => [
    {label: <><TagOutlined/> {t('keyword','Keyword')}</>, value: 'keyword'},
    {label: <><UserOutlined/> {t('person','Person')}</>, value: 'person'},
    {label: <><CalendarOutlined/> {t('event','Event')}</>, value: 'event'},
    {label: <><ShoppingCartOutlined/> {t('product','Product')}</>, value: 'product'},
    {label: <><ArtworkIcon/> {t('artwork','Artwork')}</>, value: 'artwork'},
];

const InventoryNumberField = ({setFieldValue, onStatusChange}) => {
    const {t} = useTranslation();
    const [checking, setChecking] = useState(false);
    const [status, setStatus] = useState(null); // null | 'ok' | 'taken'

    const doCheck = useRef(
        _.debounce((value) => {
            if (!value?.trim()) {
                setChecking(false);
                setStatus(null);
                onStatusChange?.(null);
                return;
            }
            setChecking(true);
            api.get('/api/tags/check_name', {params: {name: value.trim()}}).then(res => {
                setChecking(false);
                const s = res.data.exists ? 'taken' : 'ok';
                setStatus(s);
                onStatusChange?.(s);
            }).catch(() => {
                setChecking(false);
                setStatus(null);
                onStatusChange?.(null);
            });
        }, 400)
    ).current;

    const suffix = checking ? <LoadingOutlined style={{color: '#999'}}/> :
        status === 'ok' ? <CheckCircleOutlined style={{color: '#52c41a'}}/> :
        status === 'taken' ? <CloseCircleOutlined style={{color: '#ff4d4f'}}/> : null;

    return (
        <FormItem
            name='artwork_inventory_number'
            label={t('inventory-number','Inventory #')}
            required
            help={status === 'taken' ? t('inventory-number-taken', 'This inventory number already exists') : undefined}
            validateStatus={status === 'taken' ? 'error' : undefined}
        >
            <Input
                name='artwork_inventory_number'
                autoComplete='nope'
                suffix={suffix}
                onChange={e => {
                    const val = e.target.value;
                    setFieldValue('artwork_inventory_number', val);
                    setStatus(null);
                    onStatusChange?.(null);
                    doCheck(val);
                }}
            />
        </FormItem>
    );
}

/**
 * Shared form fields for creating a new tag.
 * Used by NewTagButton (Taxonomy.jsx) and AddTaxonomyTag (TaxonomyTree.jsx).
 *
 * Expects to be rendered inside a Formik form with `values` and `setFieldValue` in scope.
 * `onInventoryStatus` callback receives 'ok' | 'taken' | null for parent button state.
 */
export const NewTagTypeFields = ({values, setFieldValue, hideTypeSelect, onInventoryStatus}) => {
    const {t} = useTranslation();
    const locale = getLocale();

    return (
        <>
            {!hideTypeSelect && (
                <FormItem name='sub_type' label={t('type','Type')}>
                    <Radio.Group
                        options={tagTypeOptions(t)}
                        optionType="button"
                        size="small"
                        value={values.sub_type}
                        onChange={e => setFieldValue('sub_type', e.target.value)}
                    />
                </FormItem>
            )}

            {values.sub_type === 'event' && (
                <FormItem name='date_start' label={t('date','Date')}>
                    <DatePicker.RangePicker
                        value={[values.date_start ? day(values.date_start) : null, values.date_end ? day(values.date_end) : null]}
                        onChange={(vals) => {
                            setFieldValue('date_start', vals?.[0]);
                            setFieldValue('date_end', vals?.[1]);
                        }}
                        popupStyle={{zIndex: 9999}}
                        locale={locale}
                    />
                </FormItem>
            )}

            {values.sub_type === 'product' && (
                <>
                    <FormItem name='gtin' label={'GTIN'}>
                        <Input name='gtin' autoComplete='nope'/>
                    </FormItem>
                    <FormItem name='sku' label={'SKU'}>
                        <Input name='sku' autoComplete='nope'/>
                    </FormItem>
                </>
            )}

            {values.sub_type === 'artwork' && (
                <>
                    <FormItem name='artwork_title' label={t('title','Title')}>
                        <Input name='artwork_title' autoComplete='nope'/>
                    </FormItem>
                    <FormItem name='artwork_creator' label={t('creator','Creator')}>
                        <Input name='artwork_creator' autoComplete='nope'/>
                    </FormItem>
                    <FormItem name='artwork_date_created' label={t('date-created','Date Created')}>
                        <Input name='artwork_date_created' autoComplete='nope'/>
                    </FormItem>
                    <FormItem name='artwork_medium' label={t('medium','Medium')}>
                        <Input name='artwork_medium' autoComplete='nope'/>
                    </FormItem>
                    <FormItem name='artwork_style_period' label={t('style-period','Style/Period')}>
                        <Input name='artwork_style_period' autoComplete='nope'/>
                    </FormItem>
                    <FormItem name='artwork_source' label={t('source','Source')}>
                        <Input name='artwork_source' autoComplete='nope'/>
                    </FormItem>
                    <InventoryNumberField setFieldValue={setFieldValue} onStatusChange={onInventoryStatus}/>
                </>
            )}
        </>
    );
}

export const newTagValidationSchema = Yup.object({
    name: Yup.string().required(''),
    artwork_inventory_number: Yup.string().when('sub_type', {
        is: 'artwork',
        then: schema => schema.required('Inventory # is required for artwork tags'),
    }),
});
